import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportFilterBar from "./ReportFilterBar";
import ReportTable from "./ReportTable";
import Pagination from "../Pagination/Pagination";
import { getProfitReport } from "../../api/reports";
import { formatDateIST, formatINR, formatPercent, sumBy, toNumber } from "./reportUtils";

const ProfitLossReport = () => {
  const [, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    page: 1,
    limit: 10,
    q: "",
    view: "product",
    sortBy: "revenue",
    sortOrder: "desc",
    lossOnly: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [reportTotals, setReportTotals] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchProfitReport = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getProfitReport({
          ...filters,
          lossOnly: String(filters.lossOnly),
        });
        if (cancelled) return;

        const reportRows = response?.data?.rows || response?.rows || [];
        setRows(reportRows);
        setReportTotals(response?.data?.totals || response?.totals || {});
        setPagination(
          response?.pagination || {
            page: filters.page,
            limit: filters.limit,
            total: reportRows.length,
            totalPages: 1,
          },
        );
      } catch (fetchError) {
        if (cancelled) return;
        setRows([]);
        setReportTotals({});
        setPagination({ page: 1, limit: filters.limit, total: 0, totalPages: 1 });
        setError(
          fetchError?.response?.data?.message ||
            fetchError.message ||
            "Failed to load profit and loss report.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfitReport();
    return () => {
      cancelled = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.q,
    filters.view,
    filters.lossOnly,
    filters.sortBy,
    filters.sortOrder,
    filters.page,
    filters.limit,
  ]);

  const filteredRows = rows;
  const currentRows = rows;

  const productRows = useMemo(() => {
    const map = {};

    filteredRows.forEach((row) => {
      const key = row.product;

      if (!map[key]) {
        map[key] = {
          product: row.product,
          category: row.category,
          unitsSold: 0,
          revenueExGst: 0,
          productCost: 0,
          discount: 0,
          refund: 0,
          netProfit: 0,
        };
      }

      map[key].unitsSold += toNumber(row.unitsSold);
      map[key].revenueExGst += toNumber(row.revenueExGst);
      map[key].productCost += toNumber(row.productCost);
      map[key].discount += toNumber(row.discount);
      map[key].refund += toNumber(row.refund);
      map[key].netProfit += toNumber(row.netProfit);
    });

    return Object.values(map).map((row) => ({
      ...row,
      marginPercent: row.revenueExGst > 0 ? (row.netProfit / row.revenueExGst) * 100 : 0,
    }));
  }, [filteredRows]);

  const categoryRows = useMemo(() => {
    const map = {};

    filteredRows.forEach((row) => {
      const key = row.category;

      if (!map[key]) {
        map[key] = {
          category: row.category,
          orders: 0,
          unitsSold: 0,
          revenueExGst: 0,
          productCost: 0,
          discount: 0,
          refund: 0,
          netProfit: 0,
        };
      }

      map[key].orders += 1;
      map[key].unitsSold += toNumber(row.unitsSold);
      map[key].revenueExGst += toNumber(row.revenueExGst);
      map[key].productCost += toNumber(row.productCost);
      map[key].discount += toNumber(row.discount);
      map[key].refund += toNumber(row.refund);
      map[key].netProfit += toNumber(row.netProfit);
    });

    return Object.values(map).map((row) => ({
      ...row,
      marginPercent: row.revenueExGst > 0 ? (row.netProfit / row.revenueExGst) * 100 : 0,
    }));
  }, [filteredRows]);

  const activeRows =
    filters.view === "product"
      ? rows.length && rows[0].product !== undefined
        ? productRows
        : rows
      : filters.view === "category"
        ? rows.length && rows[0].category !== undefined && rows[0].orders !== undefined
          ? rows
          : categoryRows
        : currentRows;

  const getColumns = () => {
    if (filters.view === "product") {
      return [
        {
          key: "product",
          label: "Product",
          sortable: true,
          minWidth: 220,
        },
        {
          key: "category",
          label: "Category",
          sortable: true,
          minWidth: 140,
        },
        {
          key: "unitsSold",
          label: "Units Sold",
          sortable: true,
          className: "text-center",
        },
        {
          key: "revenueExGst",
          label: "Revenue (ex-GST)",
          sortable: true,
          render: (row) => formatINR(row.revenueExGst),
        },
        {
          key: "productCost",
          label: "Product Cost",
          sortable: true,
          render: (row) => formatINR(row.productCost),
        },
        {
          key: "discount",
          label: "Discount",
          sortable: true,
          render: (row) => formatINR(row.discount),
        },
        {
          key: "refund",
          label: "Refund",
          sortable: true,
          render: (row) => formatINR(row.refund),
        },
        {
          key: "netProfit",
          label: "Net Profit",
          sortable: true,
          render: (row) => (
            <span className={row.netProfit < 0 ? "report-loss" : "report-profit"}>
              {formatINR(row.netProfit)}
            </span>
          ),
        },
        {
          key: "marginPercent",
          label: "Margin %",
          sortable: true,
          render: (row) => formatPercent(row.marginPercent),
        },
      ];
    }

    if (filters.view === "category") {
      return [
        {
          key: "category",
          label: "Category",
          sortable: true,
          minWidth: 180,
        },
        {
          key: "orders",
          label: "Orders",
          sortable: true,
          className: "text-center",
        },
        {
          key: "unitsSold",
          label: "Units Sold",
          sortable: true,
          className: "text-center",
        },
        {
          key: "revenueExGst",
          label: "Revenue (ex-GST)",
          sortable: true,
          render: (row) => formatINR(row.revenueExGst),
        },
        {
          key: "productCost",
          label: "Product Cost",
          sortable: true,
          render: (row) => formatINR(row.productCost),
        },
        {
          key: "discount",
          label: "Discount",
          sortable: true,
          render: (row) => formatINR(row.discount),
        },
        {
          key: "refund",
          label: "Refund",
          sortable: true,
          render: (row) => formatINR(row.refund),
        },
        {
          key: "netProfit",
          label: "Net Profit",
          sortable: true,
          render: (row) => (
            <span className={row.netProfit < 0 ? "report-loss" : "report-profit"}>
              {formatINR(row.netProfit)}
            </span>
          ),
        },
        {
          key: "marginPercent",
          label: "Margin %",
          sortable: true,
          render: (row) => formatPercent(row.marginPercent),
        },
      ];
    }

    return [
      {
        key: "orderId",
        label: "Order ID",
        sortable: true,
      },
      {
        key: "customer",
        label: "Customer",
        sortable: true,
      },
      {
        key: "quantity",
        label: "Quantity",
        sortable: true,
        className: "text-center",
      },
      {
        key: "revenue",
        label: "Revenue",
        sortable: true,
        render: (row) => formatINR(row.revenue),
      },
      {
        key: "cogs",
        label: "COGS",
        sortable: true,
        render: (row) => formatINR(row.cogs),
      },
      {
        key: "profit",
        label: "Profit / Loss",
        sortable: true,
        render: (row) => (
          <span className={row.profit < 0 ? "report-loss" : "report-profit"}>
            {formatINR(row.profit)}
          </span>
        ),
      },
    ];
  };

  const getTotals = () => {
    return {
      quantity: {
        value: reportTotals.quantity ?? sumBy(activeRows, "quantity"),
        format: "number",
      },
      revenue: {
        value: reportTotals.revenue ?? sumBy(activeRows, "revenue"),
        format: "inr",
      },
      cogs: {
        value: reportTotals.cogs ?? sumBy(activeRows, "cogs"),
        format: "inr",
      },
      profit: {
        value: reportTotals.profit ?? sumBy(activeRows, "profit"),
        format: "inr",
      },
      orders: {
        value: reportTotals.orders ?? sumBy(activeRows, "orders"),
        format: "number",
      },
    };
  };

  const updateFilters = (nextFilters) => {
    setFilters(nextFilters);
  };

  const handleSort = (sortBy, sortOrder) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1,
    }));
  };

  return (
    <div className="container-fluid p-4 report-page">
      <div className="d-flex justify-content-end mb-3">
        <button type="button" className="btn btn-success" onClick={() => {}}>
          Export CSV
        </button>
      </div>
      <div className="card p-3">
        <ReportFilterBar
          filters={filters}
          onChange={updateFilters}
          onReset={updateFilters}
          searchPlaceholder="Search order, product or category..."
          filterSlotInline
          filterSlot={
            <div className="col-12 col-md-3 col-lg-2">
              <label htmlFor="loss-only" className="form-label">
                Loss Filter
              </label>

              <select
                id="loss-only"
                className="form-select"
                value={filters.lossOnly ? "true" : "false"}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    lossOnly: e.target.value === "true",
                    page: 1,
                  }))
                }
              >
                <option value="false">All</option>
                <option value="true">Loss Only</option>
              </select>
            </div>
          }
        />

      <ReportTable
          columns={getColumns()}
          rows={activeRows}
          totals={getTotals()}
          loading={loading}
          error={error}
          page={filters.page}
          totalPages={pagination.totalPages || 1}
          total={pagination.total ?? filteredRows.length}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={handleSort}
          onPageChange={(page) =>
            setFilters((prev) => ({
              ...prev,
              page: Math.min(Math.max(page, 1), pagination.totalPages || 1),
            }))
          }
          rowKey={
            filters.view === "order"
              ? "orderId"
              : filters.view === "product"
                ? "product"
                : "category"
        }
      />

      <Pagination
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        currentPage={pagination.page}
        onPageChange={(page) =>
          setFilters((prev) => ({ ...prev, page }))
        }
      />
    </div>
    </div>
  );
};

export default ProfitLossReport;
